import _ from 'lodash';
import template from './lprEvents.html';


export default {
  name: 'lprEvents',
  template,
  controller: LprEventsController,
  controllerAs: 'LprEventsCtrl',
  bindings: {
    options: '<',
    queryParams: '<',
    defaultTableOptions: '<'
  }
};

/* @ngInject */
function LprEventsController(
  $log,
  $filter,
  growl,
  EventInfoService,
  ModalService,
  SiteService,
  uiGridConstants,
  EquipmentInfoService,
  UserInfoService,
  Organization,
  $scope
) {
  const vm = this;

  let infiniteScrollDeregister = _.noop;
  let filterChangedDeregister = _.noop;
  let sortChangedDeregister = _.noop;
  let beginEditDeregister = _.noop;
  let afterEditDeregister = _.noop;
  let onRowSelection = _.noop;
  const LIMIT = 25;
  const FILTER_DEBOUNCE = 500;
  const SORT_DEBOUNCE = 250;
  const DEFAULT_SORT = ['eventDate', -1];
  const OPERATORS = ['>=', '<=', '!=', '>', '<', '~', '=', '^'];
  const UIGRID_OPERATORS_MAP = {
    [uiGridConstants.filter.GREATER_THAN_OR_EQUAL]: '>=',
    [uiGridConstants.filter.LESS_THAN_OR_EQUAL]: '<=',
    [uiGridConstants.filter.NOT_EQUAL]: '!=',
    [uiGridConstants.filter.GREATER_THAN]: '>',
    [uiGridConstants.filter.LESS_THAN]: '<',
    [uiGridConstants.filter.EQUAL]: '=',
    [uiGridConstants.filter.STARTS_WITH]: '^',
    [uiGridConstants.filter.CONTAINS]: '~'
  };

  let sort = [DEFAULT_SORT];
  let filter;
  let moreEventsToLoad = false;
  let metadata = {};
  let eventBackups = {};

  let sites;
  const lprEqBySite = new Map();

  vm.data = [];

  vm.userIsSuper = UserInfoService.isCurrentUserSuperAdmin();
  vm.userIsPartner = UserInfoService.isCurrentUserPartner();

  vm.gridOptions = _.defaults(getReportsConfig('lpr'), {
    data: vm.data,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    appScopeProvider: vm,
    enableFullRowSelection: true,
    enableRowHeaderSelection: false,
    infiniteScrollDown: true,
    infiniteScrollRowsFromEnd: 10,
    useExternalFiltering: true,
    useExternalSorting: true,
    multiSelect: false,
    gridFooterTemplate: [
      '<div class="ui-grid-footer-info ui-grid-grid-footer">',
      '<span>Loaded {{grid.appScope.data.length || 0}} of {{grid.appScope.totalItems}} items.</span>',
      '</div>'
    ].join(''),
    onRegisterApi: (gridApi) => {
      vm.gridApi = gridApi;
      infiniteScrollDeregister = gridApi.infiniteScroll.on.needLoadMoreData(null, infiniteScrollDownHandler, vm);
      filterChangedDeregister = gridApi.core.on.filterChanged(
        null,
        _.debounce(filterChangedHandler, FILTER_DEBOUNCE),
        vm
      );
      sortChangedDeregister = gridApi.core.on.sortChanged(
        null,
        _.debounce(sortChangedHandler, SORT_DEBOUNCE),
        vm
      );
      beginEditDeregister = gridApi.edit.on.beginCellEdit(null, (rowEntity) => {
        // only clone backup if this is the first edit, since it's 'clean'
        if (!eventBackups[rowEntity.id]) {
          eventBackups[rowEntity.id] = _.cloneDeep(rowEntity);
        }
      }, vm);
      afterEditDeregister = gridApi.edit.on.afterCellEdit(null, (rowEntity) => {
        const backup = _.get(eventBackups, rowEntity.id);
        gridApi.grid.getRow(rowEntity).isDirty = !_.isEqual(backup, rowEntity);
      }, vm);
      onRowSelection = gridApi.selection.on.rowSelectionChanged(null, (rowEntity) => {
        vm.openEventDetailsModalSettings(rowEntity.entity, vm.data);
      }, vm);
    }
  });

  vm.title = vm.gridOptions.title;

  vm.listOfSites = [];
  vm.selectedSites = [];
  vm.siteSelectCollapsed = false;
  vm.toggleSiteSelect = () => { vm.siteSelectCollapsed = !vm.siteSelectCollapsed };
  vm.disableExport = true;
  vm.totalItems = 0;
  vm.openEventDetailsModalSettings = ModalService.openEventDetailsModalSettings;

  vm.$onInit = async () => {
    vm.options = angular.copy(vm.options);

    vm.queryParams = angular.copy(vm.queryParams);

    try {
      vm.organizations = await Organization.model;
    } catch {
      vm.organizations = { array: [UserInfoService.getCurrentUser().org] };
    }
    await loadSitesAndEquipment();

    if (vm.organizations?.array?.length === 1) {
      const [{ id }] = vm.organizations.array;
      vm.updatedOrg = id;
    } else {
      $scope.$watch(() => vm.updatedOrg, async () => {
        if (angular.isDefined(vm.updatedOrg)) {
          const id = vm.updatedOrg;
          vm.selectedSites = [];
          vm.siteSelectCollapsed = false;
          determineApplicableSites(id);
        }
      });
    }

    $scope.$evalAsync(() => determineApplicableSites());

  };

  vm.generateEventsReport = generateEventsReport;
  vm.getData = getData;
  vm.onChangedSite = onChangedSite;

  vm.openEventDetailsModalSettings = ModalService.openEventDetailsModalSettings;
  vm.openCopyReportLink = ModalService.openCopyReportLink;

  vm.setRowState = async (eventObj, newState) => {
    const { id } = eventObj;
    try {
      const update = {};

      if (newState === 'flagged') {
        update.flagged = !eventObj.flagged;
      } else if (newState === 'deleted') {
        update.deleted = !eventObj.deleted;
      }

      const { data: [event] } = await EventInfoService.updateEvent(id, update);
      _.merge(eventObj, event);
      vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
    } catch (err) {
      console.error(err);
      growl.error(err.error || err.message, { title: 'Could not update transaction state' });
    }
  };

  vm.updateRow = async (eventObj) => {
    try {
      let event = {};
      const update = _.cloneDeep(eventObj);
      [event] = await EventInfoService.updateEvent(eventObj.id, update)
        .then(({ data }) => data);
      _.merge(eventObj, event);
      // reset isDirty and backup states
      vm.gridApi.grid.getRow(eventObj).isDirty = false;
      delete eventBackups[eventObj.id];
      vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
    } catch (err) {
      growl.error(err.error, { title: 'Could not update the event' });
    }
  };

  vm.cancelEdits = (eventObj) => {
    // revert back to pre-stored data before edits
    _.merge(eventObj, eventBackups[eventObj.id]);
    vm.gridApi.grid.getRow(eventObj).isDirty = false;
  };

  vm.editRowNotes = (row) => {
    if (!eventBackups[row.entity.id]) {
      eventBackups[row.entity.id] = _.cloneDeep(row.entity);
    }
    row.editingNotes = true;
  };

  vm.updateNotes = async (row) => {
    try {
      const { entity: event } = row;
      const [updated = {}] = await EventInfoService.updateEvent(event.id, { notes: event.notes })
        .then(({ data }) => data);
      _.merge(event, updated);
      row.editingNotes = false;
      delete eventBackups[event.id];
      vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
    } catch (err) {
      growl.error(err.error, { title: 'Could not update the event' });
    }
  };

  vm.cancelNotesEdit = (row) => {
    const { entity: event } = row;
    _.merge(event, { notes: '' }, eventBackups[event.id]);
    delete eventBackups[event.id];
    row.editingNotes = false;
    vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.EDIT);
  };

  async function infiniteScrollDownHandler() {
    const events = await fetchAdditionalEvents();
    vm.gridApi.infiniteScroll.saveScrollPercentage();
    vm.data.push(...events);
    return vm.gridApi.infiniteScroll.dataLoaded(false, moreEventsToLoad);
  }

  function filterChangedHandler() {
    const columns = vm.gridApi.grid.getOnlyDataColumns();
    const newFilter = _.reduce(columns, (acc, col) => {
      _.forEach(col.filters || [], (fil) => {
        if (fil.term) {
          // Check that the column is exclusively numbers, if so search mongo for exact number. no partials
          let operator = col.colDef.type === 'number' ? '=' :
            fil?.condition ? UIGRID_OPERATORS_MAP[fil.condition] : '^'; // default bc it uses indexes if present
          let term = fil.term;
          _.forEach(OPERATORS, (op) => {
            let ret = true;
            if (_.startsWith(term, op)) {
              operator = op;
              term = term.replace(op, '');
              ret = false;
            }
            return ret;
          });
          if (term.length) {
            acc.push(`${col.field}${operator}${term}`);
          }
        }
      });
      return acc;
    }, []).join('|');

    if (newFilter !== filter) {
      filter = newFilter;
      generateEventsReport();
    }
  }

  function sortChangedHandler(_grid, sortColumns) {
    sort = sortColumns.length ?
      _.map(sortColumns, col => [col.field, col.sort.direction === 'asc' ? 1 : -1]) :
      [DEFAULT_SORT];

    generateEventsReport();
  }

  async function fetchAdditionalEvents(limit = LIMIT) {
    let events = [];
    if (moreEventsToLoad) {
      ({ metadata, events } = await getData(limit, vm.data.length));
      const total = metadata.total || 0;
      moreEventsToLoad = vm.data.length + events.length < total;
      vm.totalItems = total;
    }
    return events;
  }

  async function getData(limit = LIMIT, skip = 0) {
    const data = { metadata: {}, events: [] };
    const eqIds = vm.selectedSites
      .flatMap(site => [...lprEqBySite.get(site.id) || []]);
    try {
      // get events
      _.merge(
        data,
        await EventInfoService.getEventsGrouped({
          equipment: eqIds,
          startDate: vm.options.date.start.toISOString(),
          endDate: vm.options.date.end.toISOString(),
          eventNames: ['cameras/plate'],
          limit,
          skip,
          sort,
          extraCriteria: { filter }
        })
      );

      vm.gridApi.core.refresh();

    } catch (err) {
      const msg = err.error || err.data || err.message;
      growl.error(msg);
      $log.error(err);
    } finally {
      vm.isLoading = false;
    }
    return data;
  }

  async function generateEventsReport() {
    moreEventsToLoad = true;
    if (!_.size(vm.selectedSites)) {
      const msg = 'No Site selected. Please select a site and try again';
      growl.error(msg);
      return;
    }

    eventBackups = {};
    vm.data.length = 0;
    vm.data.push(...await fetchAdditionalEvents(25));
    generateReportName();
    // 'fresh' report generation, reset the scroll and let it know only more data downwards if available
    // resetScroll(moreDataAvailUp, moreDataAvailDown)
    _.get(vm, ['gridApi', 'infiniteScroll', 'resetScroll'], _.noop)(false, moreEventsToLoad);
    vm.gridApi.core.notifyDataChange(uiGridConstants.dataChange.ALL);
  }

  async function loadRemainingDataForExport() {
    vm.data.push(...await fetchAdditionalEvents(vm.totalItems));
    return vm.data;
  }

  async function loadSitesAndEquipment() {
    sites = await SiteService.get();

    // Call to make sure that populates model with correct need
    EquipmentInfoService.setNeed('reports');
    await EquipmentInfoService.getEquipmentFromServer();
    _.forEach(EquipmentInfoService.model.array, (eq) => {
      if (eq.settings?.jobs?.cloudLpr?.enabled || eq.settings?.jobs?.epicLpr?.enabled) {
        if (!lprEqBySite.has(eq.site)) {
          lprEqBySite.set(eq.site, new Set());
        }
        lprEqBySite.get(eq.site).add(eq.id);
      }
    });

    const validOrgs = new Set(sites
      .filter(site => lprEqBySite.has(site.id))
      .map(site => site.org));

    vm.invalidOrgs = vm.organizations.array.filter(org => !validOrgs.has(org.id));

  }

  function determineApplicableSites(orgId = null) {

    const validSites = _.reduce(sites, (valid, site) => {
      if (lprEqBySite.has(site.id) && (!orgId || site.org === orgId)) {
        valid.push(site);
      }
      return valid;
    }, []);

    vm.listOfSites = _.sortBy(validSites, 'name');

    safeDeleteQueryParams();
  }

  function onChangedSite(selectedSites) {
    const normalizedSites = Array.isArray(selectedSites) ? selectedSites : [];
    $scope.$evalAsync(() => {
      vm.selectedSites = normalizedSites;
      vm.siteSelectCollapsed = normalizedSites.length > 1;
      vm.disableExport = true;
      generateReportName();
    });
  }

  function safeDeleteQueryParams() {
    if (
      !vm.queryParams ||
      !vm.listOfSites ||
      !vm.listOfSites.length
    ) {
      return;
    }
    if (
      vm.queryParams.start ||
        vm.queryParams.end
    ) {
      vm.isLoading = false;
      generateEventsReport();
    }
    delete vm.queryParams;
  }

  vm.$onDestroy = () => {
    infiniteScrollDeregister();
    filterChangedDeregister();
    sortChangedDeregister();
    beginEditDeregister();
    afterEditDeregister();
    onRowSelection();
  };

  function toColObj(properties) {
    return _.merge(
      {},
      {
        cellTooltip: true,
        headerTooltip: true,
        width: 225,
        type: 'string',
        headerCellClass: 'text-center',
        filter: {
          condition: uiGridConstants.filter.CONTAINS
        }
      },
      properties
    );
  }

  function getReportsConfig(reportType = 'lpr') {
    vm.getRowStyle = ({ meta: { state } = {} }) => state === 'approved' ?
      'text-success bg-success' :
      state === 'void' ?
        'text-muted' :
        'text-danger bg-danger';
    return {
      lpr: {
        title: 'License Plate Recognition Report',
        enableCellEdit: true,
        enableFiltering: true,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableGridMenu: true,
        hidePinRight: true,
        showColumnFooter: true,
        showGridFooter: true,
        exporterMenuExcel: false,
        exporterMenuPdf: false,
        exporterFieldCallback: lprExportFieldCallback,
        exporterCsvFilename: 'Lpr.csv',
        exporterAllDataFn: loadRemainingDataForExport,
        rowHeight: 130,
        excessRows: 10,
        columnDefs: [
          toColObj({
            name: 'actions',
            displayName: 'Actions',
            cellClass: 'text-center',
            enableSorting: false,
            enableColumnResizing: false,
            enableFiltering: false,
            enableCellEdit: false,
            enableHiding: false,
            hidePinRight: true,
            hidePinLeft: true,
            pinnedLeft: true,
            enableColumnMenu: false,
            enableColumnMoving: false,
            exporterSuppressExport: true,
            width: 80,
            visible: vm.userIsSuper,
            cellTemplate:
                `<div class="grid-action-cell text-center" ng-if="grid.appScope.userIsSuper">
                  <span ng-if="!row.isDirty">
                    <button class="btn btn-sm"
                      ng-class="row.entity.deleted ? 'btn-warning' : 'btn-danger'"
                      data-toggle="tooltip"
                      style="margin-left: 0px"
                      title="{{row.entity.deleted ? 'Undo Delete' : 'Delete Event'}}"
                      ng-click="grid.appScope.setRowState(row.entity, 'deleted');"
                    >
                      <i
                        class="far fa-sm flat fa-trash"
                      ></i>
                    </button>
                  </span>
                </div>`,
          }),
          {
            displayName: 'Flag',
            name: 'flagged',
            type: 'boolean',
            cellTooltip: true,
            enableCellEdit: false,
            width: 105,
            cellTemplate:
              `<div class="event-flag" ng-click="grid.appScope.setRowState(row.entity, 'flagged');
              $event.stopPropagation();">
                <i ng-class="row.entity.flagged ? 'active' : ''" class="fad fa-star fa-swap-opacity fa-2x fa-fw"></i>
              </div>`,
            filter: {
              selectOptions: [
                { value: '=true', label: 'Flagged' },
                { value: '=false', label: 'Not Flagged' },
              ],
              type: uiGridConstants.filter.SELECT
            },
          },
          toColObj({
            displayName: 'Snapshot',
            name: 'data.snapshot',
            enableCellEdit: false,
            enableFiltering: false,
            cellTemplate: `<div
                class='ui-grid-cell-contents'
                style=" position: relative;
                z-index:1 !important;
                width: 160px;
                margin: auto;"
                data-toggle="tooltip">
                  <image-slider images="row.entity.data.media.thumbs"></image-slider>
                </div>`,
          }),
          toColObj({
            displayName: 'Date/Time',
            enableFiltering: false,
            name: 'eventDate',
            enableCellEdit: false,
            cellTooltip: true,
            type: 'date',
            cellFilter: "date: 'medium'"
          }),
          toColObj({
            displayName: 'Description',
            enableCellEdit: false,
            name: 'description',
            width: 600
          }),
          toColObj({
            displayName: 'Plate',
            enableCellEdit: false,
            name: 'data.plate.value',
          }),
          toColObj({
            displayName: 'Region',
            enableCellEdit: false,
            name: 'data.region.value',
          }),
          toColObj({
            displayName: 'Make',
            enableCellEdit: false,
            name: 'data.make_model.make',
          }),
          toColObj({
            displayName: 'Model',
            enableCellEdit: false,
            name: 'data.make_model.model',
          }),
          toColObj({
            displayName: 'Color',
            enableCellEdit: false,
            name: 'data.color.value',
          }),
          toColObj({
            displayName: 'Vehicle Type',
            enableCellEdit: false,
            name: 'data.vehicle.type',
          }),
          toColObj({
            displayName: 'Orientation',
            enableCellEdit: false,
            name: 'data.orientation.value',
          }),
          toColObj({
            displayName: 'Detection Confidence',
            enableCellEdit: false,
            name: 'data.detectionConfidence',
          }),
          toColObj({
            displayName: 'Text Confidence',
            enableCellEdit: false,
            name: 'data.plate.score',
          }),
          toColObj({
            displayName: 'Tags',
            enableCellEdit: false,
            name: 'data.tags',
            cellTemplate: `<div
            class='ui-grid-cell-contents'>
              <span ng-repeat="tag in row.entity.data.tags">
                {{tag}}<span ng-if="!$last">, </span>
              </span>
            </div>`,
          }),
          toColObj({
            displayName: 'Notes',
            name: 'notes',
            width: 240,
            cellTemplate: `
              <div style="text-align: center; padding: 1rem" ng-click="$event.stopPropagation()">
                <textarea
                  class="form-control"
                  ng-model="row.entity.notes"
                  ng-disabled="!row.editingNotes"
                  autofocus="row.editingNotes"
                  ng-click="$event.stopPropagation();"></textarea>
                <div ng-switch="row.editingNotes">
                  <span ng-switch-when="true">
                    <button type="button" class="btn btn-sm"
                      ng-click="$event.stopPropagation(); grid.appScope.cancelNotesEdit(row);">
                      <i class="fad fa-times flat"></i>
                    </button>
                    <button type="button" class="btn btn-sm"
                      ng-click="$event.stopPropagation(); grid.appScope.updateNotes(row);">
                      <i class="fad fa-save"></i>
                    </button>
                  </span>
                  <span ng-switch-default>
                    <button type="button" class="btn btn-sm"
                      ng-click="$event.stopPropagation(); grid.appScope.editRowNotes(row);">
                      <i class="fad fa-edit"></i>
                    </button>
                  </span>
                </div>
              </div>
            `
          }),
          toColObj({
            displayName: 'Event ID',
            name: 'id',
            enableCellEdit: false,
          }),
        ],
      },
    }[reportType];
  }

  function lprExportFieldCallback(grid, row, col, value) {
    return col.colDef.exportFormatter?.(grid, row, col, value) ?? value;
  }

  function generateReportName() {
    const [start, end] = [vm.options.date.start, vm.options.date.end].map(date => $filter('date')(date, 'yyyyMMdd'));
    const timestamp = start === end ? start : `${start}_${end}`;
    const whichSite = vm.selectedSites.length > 1 ? 'multipleSites' : _.snakeCase(vm.selectedSites[0]?.name);
    vm.gridOptions.exporterCsvFilename = `LPR_${whichSite}_${timestamp}.csv`;
  }
}
